import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import '../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { AlertCircle, ChevronDown, X } from 'lucide-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import HelperFunctions from '../../utils/helperFunctions';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  DeviceSensorData,
  DeviceReading,
  GetDeviceSensorDataRequest,
  DeviceDashboardDto,
} from '../../services/deviceApi';
import { DevicesContext } from '../../contexts/DevicesContext';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { timeRanges, options } from '../../utils/helperFunctions';
import { ParameterDtoPreferred } from '../../services/deviceApi';
import '../../styles/pages/livedata.css';
import '../../styles/pages/device-list.css';
import { getUserTimezone } from '../../utils/timezoneUtils';
import { isEqual } from 'lodash';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import { useNavigate } from 'react-router-dom';

interface GridDataRow {
  date: string;
  deviceName?: string;
  deviceId?: number;
  parameters?: Record<string, {
    value: number;
    unit: string;
    parameterName: string;
    parameterDisplayName: string;
  }>;
  aqiValue: number;
  pm25: number;
  no2: number;
  pm10: number;
  so2: number;
  o3: number;
  co: number;
  humidity: number;
  temperature: number;
}

 
 

const LiveDataReport: React.FC = () => {
  const navigate = useNavigate();
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();
  const isRestoring = useRef(true);
  const previousDeviceRef = useRef<DeviceDashboardDto | null>(null);
  // Devices Context
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('LiveDataReport must be used within a DevicesProvider');
  }

  const {
    devicesForDashboard,
    parametersForDashboard,
    getDevicesForDashboard,
    getParametersForDashboard,
    getDeviceSensorData,
    // exportDeviceData,
  } = devicesContext;

  // User Selected Context
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }

  const {
    userSelectedLiveDataTimeRange,
    setUserSelectedLiveDataTimeRange,
    customFromDate,
    customToDate,
    setCustomFromDate,
    setCustomToDate,
  } = userSelectedContext;

  const [sensorData, setSensorData] = useState<GridDataRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const gridRef = React.useRef<AgGridReact>(null);
  const isFetchingRef = React.useRef(false);
  const getDeviceSensorDataRef = React.useRef(getDeviceSensorData);
  const getDevicesForDashboardRef = React.useRef(getDevicesForDashboard);
  const getParametersForDashboardRef = React.useRef(getParametersForDashboard);
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [customReportsDateError, setCustomReportsDateError] = useState('');
  // const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set([0]));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [showTimeRangeModal, setShowTimeRangeModal] = useState(false);
  const [customReportsDateChanged, setCustomReportsDateChanged] = useState(false);
  const theme = document.documentElement.getAttribute("data-theme") ?? "light";

  // Update refs when functions change
  React.useEffect(() => {
    getDeviceSensorDataRef.current = getDeviceSensorData;
    getDevicesForDashboardRef.current = getDevicesForDashboard;
    getParametersForDashboardRef.current = getParametersForDashboard;
  }, [getDeviceSensorData, getDevicesForDashboard, getParametersForDashboard]);

  // Mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Toggle card expansion
  // const toggleCardExpansion = (index: number) => {
  //   const newExpandedCards = new Set(expandedCards);
  //   if (newExpandedCards.has(index)) {
  //     newExpandedCards.delete(index);
  //   } else {
  //     newExpandedCards.add(index);
  //   }
  //   setExpandedCards(newExpandedCards);
  // };

  useEffect(() => {
    if (devicesForDashboard.length > 0 && selectedDeviceId === null) {
      const isActiveDevice = devicesForDashboard.find(device => device.isActive == true);

      if(isActiveDevice) {
        setSelectedDeviceId(isActiveDevice.deviceId);
      } else {
        setSelectedDeviceId(devicesForDashboard[0].deviceId);
      }
    }
  }, [devicesForDashboard, selectedDeviceId]);

  const deviceDetails = useMemo(() => {
    if (!selectedDeviceId) return null;

    // find the device in the devicesForDashboard
    const found = devicesForDashboard.find(
      (d: DeviceDashboardDto) => d.deviceId === selectedDeviceId
    );
  
    if (!previousDeviceRef.current || !isEqual(previousDeviceRef.current, found)) {
      previousDeviceRef.current = found || null;
    }
  
    return previousDeviceRef.current;
  }, [devicesForDashboard, selectedDeviceId]);

  const parametersForDevice : ParameterDtoPreferred[] = useMemo(() => {
    if (!deviceDetails) return [];
    const deviceParameterids = Object.keys(deviceDetails.parameters);
    return parametersForDashboard.filter(parameter =>
      deviceParameterids.includes(parameter.driverID.toString())
    );
  }, [deviceDetails, parametersForDashboard]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setShowDeviceDropdown(false);
        setShowTimeRangeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Function to transform DeviceSensorData to grid format with dynamic parameters
  const transformSensorDataToGrid = React.useCallback(
    (deviceData: DeviceSensorData[]): any[] => {
      const gridData: any[] = [];

      deviceData.forEach(device => {
        // Group readings by recordedAt timestamp
        const readingsByTime = device.readings.reduce(
          (acc, reading) => {
            const timeKey = reading.recordedAt;
            if (!acc[timeKey]) {
              acc[timeKey] = [];
            }
            acc[timeKey].push(reading);
            return acc;
          },
          {} as Record<string, DeviceReading[]>
        );

        // Create grid rows for each timestamp
        Object.entries(readingsByTime).forEach(([timestamp, readings]) => {


          const row: any = {
            date: timestamp,
            deviceName: device.deviceName,
            deviceId: device.deviceId,
            parameters: {}, // dynamic fields later
          };

          // Map readings to dynamic parameter fields
          readings.forEach(reading => {
            // Store parameter data in a structure similar to DeviceDashboardDto
            row.parameters[reading.parameterDriverId] = {
              value: reading.parameterValue,
              unit: reading.parameterUnit,
              parameterName: reading.parameterName,
              parameterDisplayName: reading.parameterDisplayName,
            };
          });

          gridData.push(row);
        });
      });

      // Sort by date (newest first)
      return gridData.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    },
    []
  );

  // Fetch sensor data
  const fetchSensorData = React.useCallback(async () => {
    if (isFetchingRef.current) {
      return; // Prevent multiple simultaneous calls
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);

      // Check if device is selected
      if (!selectedDeviceId) {
        setSensorData([]);
        setTotalPages(0);
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Calculate date range based on selected time range
      let fromDate: string;
      let toDate: string = new Date().toISOString();
      if (userSelectedLiveDataTimeRange === '12h') {
        fromDate = new Date(
          new Date().setHours(new Date().getHours() - 12)
        ).toISOString();
      } else if (userSelectedLiveDataTimeRange === '24h') {
        fromDate = new Date(
          new Date().setDate(new Date().getDate() - 1)
        ).toISOString();
      } else if (userSelectedLiveDataTimeRange === '7d') {
        fromDate = new Date(
          new Date().setDate(new Date().getDate() - 7)
        ).toISOString();
      } else if (userSelectedLiveDataTimeRange === '30d') {
        fromDate = new Date(
          new Date().setDate(new Date().getDate() - 30)
        ).toISOString();
      } else if (userSelectedLiveDataTimeRange === 'custom') {
        fromDate = new Date(customFromDate).toISOString();
        toDate = new Date(customToDate).toISOString();
      } else {
        fromDate = new Date(
          new Date().setHours(new Date().getHours() - 12)
        ).toISOString();
      }

      // Fetch sensor data for the selected device
      const allSensorData: DeviceSensorData[] = [];
      try {
          const request: GetDeviceSensorDataRequest = {
            DeviceId: selectedDeviceId,
            FromDate: fromDate,
            ToDate: toDate,
          };

          const deviceSensorData =
            await getDeviceSensorDataRef.current(request);
          if (deviceSensorData) {
            allSensorData.push(deviceSensorData);
          }
        } catch (error) {
          console.error(
            `Error fetching data for device ${selectedDeviceId}:`,
            error
          );
        }

      // Transform data to grid format
      const gridData = transformSensorDataToGrid(allSensorData);

      // Set all data to grid - let AG-Grid handle pagination
      setSensorData(gridData);

      // Calculate total pages for custom pagination controls
      const pageSize = 50; // Match grid pagination page size
      setTotalPages(Math.ceil(gridData.length / pageSize));
    } catch (error) {
      console.error('Error fetching sensor data:', error);
      // No mock data - show empty state
      setSensorData([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    userSelectedLiveDataTimeRange,
    selectedDeviceId,
    devicesForDashboard,
    transformSensorDataToGrid,
    customReportsDateChanged,
  ]);

  // Load devices and parameters when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setInitialLoading(true);

        // Load devices if not available
        if (devicesForDashboard.length === 0) {
          console.log('Loading devices...');
          await getDevicesForDashboardRef.current();
        }

        // Load parameters if not available
        if (parametersForDashboard.length === 0) {
          console.log('Loading parameters...');
          await getParametersForDashboardRef.current();
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadInitialData();
  }, []); // Run only once on mount

  useEffect(() => {
    // Only fetch sensor data if we have devices and parameters loaded
    if (
      devicesForDashboard.length > 0 &&
      parametersForDashboard.length > 0 &&
      selectedDeviceId
    ) {
      fetchSensorData();
    }

    // Cleanup function to prevent state updates after unmount
    return () => {
      isFetchingRef.current = false;
    };
  }, [
    fetchSensorData,
    devicesForDashboard.length,
    parametersForDashboard.length,
    selectedDeviceId,
    customReportsDateChanged,
  ]);

  const goToPage = (page: number) => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToPage(page);
      setCurrentPage(page);
    }
  };

  // Update current page when AG-Grid pagination changes
  const onPaginationChanged = () => {
    if (gridRef.current?.api) {
      const currentPage = gridRef.current.api.paginationGetCurrentPage();
      const totalPages = gridRef.current.api.paginationGetTotalPages();
      setCurrentPage(currentPage);
      setTotalPages(totalPages);
    }
  };

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSelectedLiveDataTimeRange(e.target.value);
    if(e.target.value !== "custom") {
      setShowTimeRangeDropdown(false);
      isMobile && setShowTimeRangeModal(false);
    }
  };

  const handleDeviceChange = (deviceId: number) => {
    setSelectedDeviceId(deviceId);
    setShowDeviceDropdown(false);
  };

  const handleCustomReportsDateChange = () => {
    if(!customFromDate || !customToDate) {
      setCustomReportsDateError('Please select From and To dates');
      return;
    }
    
    // Extract date part (YYYY-MM-DD) and compare dates only, ignoring time
    // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM" formats
    const fromDateStr = customFromDate.includes('T') ? customFromDate.split('T')[0] : customFromDate;
    const toDateStr = customToDate.includes('T') ? customToDate.split('T')[0] : customToDate;
    
    // Compare date strings directly (YYYY-MM-DD format allows string comparison)
    if(fromDateStr >= toDateStr) {
      setCustomReportsDateError('Start date must be before end date');
      return;
    }
    setCustomReportsDateError('');
    setCustomReportsDateChanged(!customReportsDateChanged);
    setShowTimeRangeDropdown(false);
    isMobile && setShowTimeRangeModal(false);
  };

  //Cancel custom date selection
  const handleCustomReportsDateCancel = () => {
    setCustomReportsDateError('');
    setShowTimeRangeDropdown(false);
    isMobile && setShowTimeRangeModal(false);
  };

    // store the grid column state on resize, sort, move etc
    const storeColumnState = () => {
      if (isRestoring.current) return;
      if (!gridRef.current?.api) return;

      localStorage.setItem('liveDataReportColumnState', JSON.stringify(gridRef.current.api.getColumnState()));
    }
  
     // Restore the grid api column state on grid ready if state is stored in localStorage other store the state in localStorage
  const restoreColumnState = () => {
    const state = localStorage.getItem("liveDataReportColumnState");
    if (state) {
      isRestoring.current = true;
      gridRef.current?.api?.applyColumnState({
        state: JSON.parse(state),
        applyOrder: true,
      });
      setTimeout(() => {
        isRestoring.current = false;
      }, 100);
    }else {
      isRestoring.current = false;
    }
  }

   // generateColumnDefs with nulls always at bottom
const generateColumnDefs = (): ColDef[] => {
  // --- Helper comparator for numbers ---
  const nullSafeNumberComparator = (a: number | null, b: number | null) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;   // nulls → bottom
    if (b == null) return -1;  // nulls → bottom
    return a - b;
  };

  // --- Helper comparator for dates ---
  const nullSafeDateComparator = (a: number | null, b: number | null) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  };

  const baseColumns: ColDef[] = [
    {
      headerName: 'Date',
      field: 'date',
      minWidth: isMobile ? 120 : 200,
      flex: isMobile ? undefined : 2,
      width: isMobile ? 120 : undefined,
      sortable: true,
      filter: false,
      pinned: isMobile ? undefined : 'left',
      // return timestamp for sorting
      valueGetter: (params) => {
        const d = new Date(params.data.date);
        return isNaN(d.getTime()) ? null : d.getTime();
      },
      comparator: nullSafeDateComparator,
      cellStyle: { whiteSpace: 'nowrap' as const, fontSize: isMobile ? '0.75rem' : 'inherit' },
      valueFormatter: (params: any) => {
        const date = new Date(params.data.date);
        if (isMobile) {
          const timePart = date.toLocaleTimeString('en-US', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true });
          const datePart = date.toLocaleDateString('en-US', { timeZone, year: '2-digit', month: 'short', day: 'numeric' });
          return `${datePart} ${timePart}`;
        }
        return date.toLocaleString('en-US', {timeZone: timeZone, ...options});
      },
      wrapText: false
    },
  ];

  // Add AQI column if available
  const aqiParameter = parametersForDevice.find(
    (p) => p.parameterName === 'AQI'
  );
  if (aqiParameter) {
    baseColumns.push({
      headerName: 'AQI',
      field: 'aqiValue',
      minWidth: isMobile ? 75 : 150,
      flex: isMobile ? undefined : 1.5,
      width: isMobile ? 75 : undefined,
      sortable: true,
      valueGetter: (params: any) => {
        const aqi = params.data.parameters[aqiParameter.driverID];
        return aqi?.value != null ? Number(aqi.value) : null;
      },
      comparator: nullSafeNumberComparator,
      cellRenderer: (params: any) => {
        const value =
          params.value != null ? params.value.toFixed(0) : 'N/A';
        return (
          <span
            className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-md text-[var(--color-sub-title-text)] font-normal`}
            style={{ background: `${theme == 'light' ? HelperFunctions.GetParameterColor(params.value, 'AQI') : HelperFunctions.GetParameterColor(params.value, 'AQI') + 80}` }}
          >
            {value}
          </span>
        );
      },
    });
  }

  // Add dynamic parameter columns (excluding AQI as it's handled separately)
  const parameterColumns = parametersForDevice
    .filter(parameter => parameter.parameterName !== 'AQI')
    .sort((a, b) => {
      if (a.parameterName == 'PM10') return -1;
      if (b.parameterName == 'PM10') return 1;
      if (a.parameterName == 'PM2.5') return -1;
      if (b.parameterName == 'PM2.5') return 1;
      return 0;
    })
    .map(parameter => ({
      headerName: `${parameter.parameterName}${parameter.unit ? ` (${parameter.unit})` : ''}`,
      ...(isMobile ? {
        headerComponent: (props: any) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', width: '100%', flexWrap: 'wrap', justifyContent: 'flex-start' }}
            onClick={() => props.progressSort && props.progressSort()}
          >
            <span style={{ fontWeight: 600, fontSize: '0.7rem' }}>{parameter.parameterName}</span>
            {parameter.unit && <span style={{ fontSize: '0.5rem', color: '#6b7280' }}>({parameter.unit})</span>}
          </div>
        ),
      } : {
        wrapHeaderText: true,
        autoHeaderHeight: true,
      }),
      field: String(parameter.driverID),
      minWidth: isMobile ? 90 : (parameter.parameterName.length + (parameter.unit?.length ?? 0) > 10 ? 175 : 155),
      flex: isMobile ? undefined : (parameter.parameterName.length + (parameter.unit?.length ?? 0) >= 8 ? 1.75 : 1.25),
      width: isMobile ? 90 : undefined,
      sortable: true,
      valueGetter: (params: any) => {
        const param = params.data.parameters[parameter.driverID];
        return param?.value != null ? Number(param.value) : null;
      },
      comparator: nullSafeNumberComparator,
      cellRenderer: (params: any) => {
        // if(parameter.parameterName === 'Temperature' || parameter.parameterName === 'Humidity') {
        //   return <span>{params.value != null ? Math.round(params.value) : "N/A"}</span>;
        // }
        const value =
          params.value != null
            ? Number.isInteger(params.value)
              ? params.value
              : params.value < 0.01
                ? params.value.toFixed(4)
                : params.value.toFixed(2)
            : 'N/A';
        return <span>{value}</span>;
      },
    }));

  return [...baseColumns, ...parameterColumns];
}


  const columnDefs: ColDef[] = useMemo(() => generateColumnDefs(), [parametersForDevice, timeZone]);

  // Mobile Card View Component
  // const MobileCardView = () => {
  //   return (
  //     <div className="data-cards-container overflow-y-auto p-5 pt-0">
  //       <div className="data-cards-grid">
  //         {sensorData.map((data, index) => {
  //           const isExpanded = expandedCards.has(index);
  //           const aqiParameter = parametersForDevice.find(p => p.parameterName === 'AQI');
  //           const aqiValue = aqiParameter && data.parameters ? data.parameters[aqiParameter.driverID]?.value : null;
            
  //           return (
  //             <div
  //               key={index}
  //               className="bg-transparent rounded-[0.875rem] mb-2 border border-[var(--button-border-color)] p-1 dark:p-0.5"
  //             >
  //               {/* Header */}
  //               <div className={`flex justify-between rounded-[0.875rem] items-center cursor-pointer bg-[var(--mobile-card-header-background)] p-4 ${isExpanded && 'rounded-b-none border-b border-[var(--mobile-card-border-color)]'}`} onClick={() => toggleCardExpansion(index)}>
  //                 <div className="flex items-center gap-3">
  //                   {aqiValue !== null && aqiValue !== undefined && (
  //                     <div
  //                       className="w-11 h-11 p-1.5 flex items-center justify-center text-xs font-semibold rounded-md text-[var(--color-text)]"
  //                       style={{
  //                         background: HelperFunctions.GetParameterColor(
  //                           Number(aqiValue),
  //                           "AQI"
  //                         ),
  //                       }}
  //                     >
  //                       {aqiValue.toFixed(0)}
  //                     </div>
  //                   )}
  //                   <div>
  //                     <div className="font-semibold text-[var(--color-text)] text-base">
  //                       {data.deviceName}
  //                     </div>
  //                     <div className="text-sm text-[var(--color-sub-title-text)] font-normal">{new Date(data.date).toLocaleString('en-US', {timeZone: timeZone, ...options})}</div>
  //                   </div>
  //                 </div>
  //                 <button>
  //                   {isExpanded ? (
  //                     <ChevronUp className="w-6 h-6 text-[var(--color-sub-title-text)]" />
  //                   ) : (
  //                     <ChevronDown className="w-6 h-6 text-[var(--color-sub-title-text)]" />
  //                   )}
  //                 </button>
  //               </div>
          
  //               {/* Expanded Sensor Grid */}
  //               {isExpanded && (
  //                 <div className="p-4">
  //                   <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-3">
  //                     {parametersForDevice
  //                       .filter((parameter) => parameter.parameterName !== "AQI")
  //                       .map((param, idx) => {
  //                         const paramData = data.parameters?.[param.driverID];
  //                         const paramValue =
  //                           paramData?.value != null
  //                           ? Number.isInteger(paramData.value)
  //                             ? paramData.value
  //                             : paramData.value < 0.01 ? paramData.value.toFixed(3) : paramData.value.toFixed(2)
  //                           : "N/A";
          
  //                         return (
  //                           <div
  //                             key={idx}
  //                             className="flex items-center justify-between text-sm text-[var(--color-text)] whitespace-nowrap gap-0.5"
  //                           >
  //                             <div className="flex items-center gap-0 min-w-0 flex-shrink">
  //                               <span className="font-light whitespace-nowrap">
  //                                 {param.parameterName}
  //                               </span>
  //                               {param.unit && (
  //                                 <span className="text-[0.7rem] flex-shrink-0">
  //                                   ({param.unit})
  //                                 </span>
  //                               )}
  //                             </div>
  //                             <span className="font-semibold flex-shrink-0 text-right">
  //                               {paramValue}
  //                             </span>
  //                           </div>
  //                         );
  //                       })}
  //                   </div>
  //                 </div>
  //               )}
  //             </div>
  //           );
  //         })}
  //       </div>
  //     </div>
  //   );
  // };

  const gridOptions: GridOptions = useMemo(() => ({
    defaultColDef: {
      resizable: true,
      minWidth: isMobile ? 70 : 150,
      sortable: false,
      filter: false,
      ...(isMobile ? { headerClass: 'ag-left-aligned-header', cellStyle: { textAlign: 'left' } } : {}),
    },
    rowData: sensorData,
    columnDefs: columnDefs,
    pagination: true,
    paginationPageSize: 20, // Match the page size calculation
    suppressPaginationPanel: true,
    suppressCellFocus: true,
    domLayout: 'normal',
    rowHeight: 50,
    headerHeight: 70,
    autoHeaderHeight: true,
    // Performance optimizations for large datasets
    suppressRowHoverHighlight: false,
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    suppressDragLeaveHidesColumns: true,
    suppressColumnMoveAnimation: true,
    // Enable row virtualization for better performance
    rowModelType: 'clientSide',
    icons: {
      sortAscending: '<span style="font-size:12px;">▲</span>',
      sortDescending: '<span style="font-size:12px;">▼</span>',
    },
    onColumnResized: storeColumnState,
    onColumnPinned: storeColumnState,
    onSortChanged: storeColumnState,
    onColumnMoved: storeColumnState,
    onGridReady: () => {
      setTimeout(() => {
        restoreColumnState();
      }, 50);
    },
    onFirstDataRendered: (params) => {
      params.api.sizeColumnsToFit();
    }
  }), [columnDefs, sensorData]);

  if (initialLoading) {
    return (
      <div className="h-[70vh] loading-container">
        <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-3 animate-spin rounded-full"></div>
        <div className="text-sm text-[var(--color-sub-title-text)]">
          Loading devices and parameters...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-5 md:overflow-y-hidden overflow-y-auto flex flex-col flex-1 px-5 md:px-0">
        {/* Header Section */}
        <div className="flex justify-between lg:flex-row flex-col items-start lg:gap-0 gap-4">
          {!isMobile && (
            <div className='zoomOutLevel'>
              <h2 className="device-title">Live Data Report</h2>
              <p className="device-subtitle ml-0">
                Real-time air quality monitoring from selected station.
              </p>
            </div>
          )}

          <div
            className={
              !isMobile
                ? 'flex items-center gap-6 zoomOutLevel'
                : 'w-full grid grid-cols-2 gap-4 bg-transparent'
            }
          >
            {/* Device Filter */}
            <div className="flex items-center gap-2">
              <label className="text-base sm:block hidden font-normal text-[var(--color-text)]">
                Device:
              </label>
              {isMobile ? (
                <button
                  onClick={() => {
                    if(devicesForDashboard.length === 0) {
                      return;
                    }
                    setShowDeviceModal(true);
                  }}
                  className="flex items-center gap-2 w-full device-dropdown-select"
                >
                  <span>
                    {devicesForDashboard.find(
                      device => device.deviceId === selectedDeviceId
                    )?.deviceName || 'Select Device'}
                  </span>
                  <ChevronDown className="w-5 h-5 ml-auto" />
                </button>
              ) : (
                <div className="dropdown-container">
                  <div
                    className="dropdown-label"
                    onClick={() => {
                      if(devicesForDashboard.length === 0) {
                        return;
                      }
                      setShowDeviceDropdown(!showDeviceDropdown);
                      setShowTimeRangeDropdown(false);
                    }}
                  >
                    <div className="dropdown-label-content">
                      <p>
                        {devicesForDashboard.find(
                          device => device.deviceId === selectedDeviceId
                        )?.deviceName || 'Select Device'}
                      </p>
                    </div>
                    <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
                  </div>
                  {showDeviceDropdown && (
                    <div className="dropdown-options">
                      {devicesForDashboard.map(device => (
                        <label key={device.deviceId} className="dropdown-option">
                          <input
                            type="radio"
                            name="device"
                            value={device.deviceId}
                            checked={selectedDeviceId === device.deviceId}
                            onChange={() => handleDeviceChange(device.deviceId)}
                            className="radio-input"
                          />
                          <span className="radio-label">{device.deviceName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Time Range Dropdown */}
            {isMobile ? (
              <button
                onClick={() => setShowTimeRangeModal(true)}
                className="flex gap-2 items-center device-dropdown-select"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                  alt="calendar"
                  className="w-4 h-4"
                />
                <span>
                  {timeRanges.find(range => range.value === userSelectedLiveDataTimeRange)?.label ||
                    'Select Time Range'}
                </span>
                <ChevronDown className="w-5 h-5 ml-auto" />
              </button>
            ) : (
              <div className="dropdown-container">
                <div
                  className="dropdown-label"
                  onClick={() => {
                    setShowTimeRangeDropdown(!showTimeRangeDropdown);
                    setShowDeviceDropdown(false);
                  }}
                >
                  <div className="dropdown-label-content">
                    <img
                      src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                      alt="arrow"
                      className="mr-2"
                    />
                    <p>
                      {timeRanges.find(range => range.value === userSelectedLiveDataTimeRange)?.label}
                    </p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
                </div>
                {showTimeRangeDropdown && (
                  <div className='dropdown-options'>
                    {timeRanges.map(range => (
                      <label key={range.value} className="dropdown-option">
                        <input
                          type="radio"
                          name="timeRange"
                          value={range.value}
                          checked={userSelectedLiveDataTimeRange === range.value}
                          onChange={handleTimeRangeChange}
                          className="radio-input"
                        />
                        <span className="radio-label">{range.label}</span>
                      </label>
                    ))}

                    {userSelectedLiveDataTimeRange === "custom" && (
                      <div className="custom-time-range-container">
                        <div className="custom-time-range-inputs">
                          <div className="custom-date-field-wrapper">
                            <label htmlFor="customFromDate" className="custom-date-label">From:</label>
                            <input
                              type="date"
                              name="timeRange"
                              id="customFromDate"
                              value={customFromDate.split('T')[0]}
                              onClick={(e) => e.currentTarget.showPicker()}
                              onChange={(e) => setCustomFromDate(e.target.value)}
                              className="custom-date-input"
                            />
                          </div>
                          <div className="custom-date-field-wrapper">
                            <label htmlFor="customToDate" className="custom-date-label">To:</label>
                            <input
                              type="date"
                              name="timeRange"
                              id="customToDate"
                              value={customToDate.split('T')[0]}
                              onClick={(e) => e.currentTarget.showPicker()}
                              onChange={(e) => setCustomToDate(e.target.value)}
                              className="custom-date-input"
                            />
                          </div>
                        </div>
                        {customReportsDateError && (
                            <div className="custom-date-error-container">
                              <AlertCircle className="w-4 h-4 text-red-500" />
                              <p>{customReportsDateError}</p>
                            </div>
                        )}
                        <div className="custom-date-buttons">
                          <button onClick={handleCustomReportsDateCancel} className="custom-date-cancel-button">Cancel</button>
                          <button onClick={handleCustomReportsDateChange} className="custom-date-ok-button">Apply</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Data Cards / Desktop Grid */}
        {loading ? (
          <div className="min-h-[300px] loading-container">
            <div className="h-7 w-7 border-b-2 border-[var(--gradient-button-left-color)] mb-3 animate-spin rounded-full"></div>
            <p className="text-sm text-[var(--color-sub-title-text)]">
              Loading sensor data...
            </p>
          </div>
        ) : (
          sensorData.length != 0 && (
          //   isMobile ? (
          //   <MobileCardView />
          // ) : (
            <div className="ag-theme-alpine w-full device-view-container zoomOutLevel">
              <AgGridReact
                ref={gridRef}
                {...gridOptions}
                className="device-table-grid live-data-grid"
                onPaginationChanged={onPaginationChanged}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container h-auto flex-wrap !justify-end gap-y-2">
                  <p className="pagination-page-info text-sm text-[var(--color-sub-title-text)]">
                    Page {currentPage + 1} of {totalPages}
                  </p>

                  <button
                    disabled={currentPage <= 0}
                    className="pagination-button pagination-previous"
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                    {totalPages < 5 ? (Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`pagination-button ${currentPage === i ? 'active' : ''}`}
                        >
                          {i + 1}
                        </button>
                      ))) : (
                        <>
                          {Array.from({ length: 2 }, (_, i) => (
                            <button
                              key={i}
                              onClick={() => goToPage(i)}
                              className={`pagination-button ${currentPage === i ? 'active' : ''}`}
                            >
                              {i + 1}
                            </button>
                          ))}

                          <p className='text-[var(--color-text)]'>...</p>

                          <button
                            onClick={() => goToPage(totalPages - 2)}
                            className={`pagination-button ${currentPage === (totalPages - 2) ? 'active' : ''}`}
                          >
                            {totalPages - 1}
                          </button>

                          <button
                            onClick={() => goToPage(totalPages - 1)}
                            className={`pagination-button ${currentPage === (totalPages - 1) ? 'active' : ''}`}
                          >
                            {totalPages}
                          </button>
                        </>
                    )}

                  <button
                    disabled={currentPage >= totalPages - 1}
                    className="pagination-button pagination-next"
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        {/* // )} */}

        {/* Empty State */}
        {sensorData.length === 0 && !loading && !initialLoading && (
          <div className="error-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem', minHeight: '60vh' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
              <path d="M3 3v18h18" />
              <path d="M7 16l4-4 4 4 5-5" />
            </svg>
            <p style={{ fontSize: '1rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>No Data Available</p>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
              {!selectedDeviceId
                ? 'You haven\'t added any devices yet. Please add a device to start monitoring your air quality.'
                : 'There is no recorded data for the selected time period. Please try selecting a different date range.'}
            </p>
            {!selectedDeviceId && <button onClick={() => navigate('/add-device')} className="device-button">+ Add Your First Device </button>}
          </div>
        )}

      </div>
      
      {/* Mobile Device Selection Modal */}
      {isMobile && showDeviceModal && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowDeviceModal(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Device</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowDeviceModal(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {devicesForDashboard.map(device => (
                <label key={device.deviceId} className="mobile-modal-option">
                  <input
                    type="radio"
                    name="device"
                    value={device.deviceId}
                    checked={selectedDeviceId === device.deviceId}
                    onChange={e => {
                      setSelectedDeviceId(parseInt(e.target.value));
                      setShowDeviceModal(false);
                    }}
                    className="mobile-modal-radio"
                  />
                  <span className="mobile-modal-label">
                    {device.deviceName}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Time Range Selection Modal */}
      {isMobile && showTimeRangeModal && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowTimeRangeModal(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Time Range</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowTimeRangeModal(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {timeRanges.map(range => (
                <label key={range.value} className="mobile-modal-option">
                  <input
                    type="radio"
                    name="timeRange"
                    value={range.value}
                    checked={userSelectedLiveDataTimeRange === range.value}
                    onChange={handleTimeRangeChange}
                    className="mobile-modal-radio"
                  />
                  <span className="mobile-modal-label">{range.label}</span>
                </label>
              ))}
              {userSelectedLiveDataTimeRange === "custom" && (
                <div className="custom-time-range-container">
                  <div className="custom-time-range-inputs">
                    <div className="custom-date-field-wrapper">
                      <label htmlFor="customFromDate" className="custom-date-label">From:</label>
                      <input
                        type="date"
                        name="timeRange"
                        id="customFromDate"
                        value={customFromDate.split('T')[0]}
                        onClick={(e) => e.currentTarget.showPicker()}
                        onChange={(e) => setCustomFromDate(e.target.value)}
                        className="custom-date-input"
                      />
                    </div>
                    <div className="custom-date-field-wrapper">
                      <label htmlFor="customToDate" className="custom-date-label">To:</label>
                      <input
                        type="date"
                        name="timeRange"
                        id="customToDate"
                        value={customToDate.split('T')[0]}
                        onClick={(e) => e.currentTarget.showPicker()}
                        onChange={(e) => setCustomToDate(e.target.value)}
                        className="custom-date-input"
                      />
                    </div>
                  </div>
                  {customReportsDateError && (
                    <div className="custom-date-error-container">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <p>{customReportsDateError}</p>
                    </div>
                  )}
                  <div className="custom-date-buttons">
                    <button onClick={handleCustomReportsDateCancel} className="custom-date-cancel-button">Cancel</button>
                    <button onClick={handleCustomReportsDateChange} className="custom-date-ok-button">Apply</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveDataReport;

